import { useState, type FormEvent } from 'react';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconEdit, IconTrash } from '../components/icons';
import { Modal } from '../components/Modal';
import Pagination from '../components/Pagination';
import { useAuth } from '../auth/useAuth';
import { listOptions, labelFor, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import usePaginatedList from '../hooks/usePaginatedList';

interface CounterpartyRow {
  id: number;
  role: string;
  name: string;
  contact_email: string | null;
  contact_phone: string | null;
  notes: string | null;
}

const emptyForm = { role: '', name: '', contact_email: '', contact_phone: '', notes: '' };
// Stable reference — usePaginatedList compares filters by identity to
// detect a real filter change, so a fresh {} literal passed inline here
// would look like a new filter on every render and loop forever.
const NO_FILTERS = {};

// Tenants and borrowers, linked to Property/Loans investments from
// InvestmentForm.tsx — a satellite of Investment Management, not its own
// module (rides on the same investments.* permissions).
export function InvestmentCounterparties() {
  const { can } = useAuth();
  const { lists } = useConfig();
  const confirmAction = useConfirm();
  const { showError } = useToast();

  const canCreate = can('investments.create');
  const canUpdate = can('investments.update');
  const canArchive = can('investments.archive');

  const { data, loading, error, setPage, reload } = usePaginatedList<CounterpartyRow>('/investment-counterparties', NO_FILTERS);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CounterpartyRow | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setErrors({});
    setModalOpen(true);
  };

  const openEdit = (row: CounterpartyRow) => {
    setEditing(row);
    setForm({
      role: row.role, name: row.name,
      contact_email: row.contact_email ?? '', contact_phone: row.contact_phone ?? '', notes: row.notes ?? '',
    });
    setErrors({});
    setModalOpen(true);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const payload = { ...form, contact_email: form.contact_email || undefined, contact_phone: form.contact_phone || undefined };
    try {
      if (editing) await api.put(`/investment-counterparties/${editing.id}`, payload);
      else await api.post('/investment-counterparties', payload);
      setModalOpen(false);
      reload();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (row: CounterpartyRow) => {
    if (!(await confirmAction(`Delete "${row.name}"?`))) return;
    try {
      await api.delete(`/investment-counterparties/${row.id}`);
      reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Counterparties</h1>
        {canCreate && (
          <button type="button" className="btn btn-primary" onClick={openAdd}>
            + Add
          </button>
        )}
      </header>
      <p className="muted">Tenants and borrowers linked to Property and Loans investments.</p>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Email</th>
                <th>Phone</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={5} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <strong>{row.name}</strong>
                    </td>
                    <td>{labelFor(lists, 'investment_counterparty_role', row.role)}</td>
                    <td className="muted">{row.contact_email ?? '—'}</td>
                    <td className="muted">{row.contact_phone ?? '—'}</td>
                    <td className="right">
                      {canUpdate && <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(row)} />}
                      {canArchive && (
                        <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(row)} />
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="muted center">
                    No counterparties yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>

      {modalOpen && (
        <Modal title={editing ? 'Edit counterparty' : 'Add counterparty'} onClose={() => setModalOpen(false)}>
          <form onSubmit={submit} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label>
              Role *
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} required>
                <option value="">Select…</option>
                {listOptions(lists, 'investment_counterparty_role').map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.role && <small className="error">{errors.role[0]}</small>}
            </label>
            <label>
              Name *
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              {errors.name && <small className="error">{errors.name[0]}</small>}
            </label>
            <label>
              Contact email
              <input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
              {errors.contact_email && <small className="error">{errors.contact_email[0]}</small>}
            </label>
            <label>
              Contact phone
              <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
            </label>
            <label className="span-2">
              Notes
              <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setModalOpen(false)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
